import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import Grid from '@mui/material/Grid'
import {makeStyles} from '@mui/styles'
import TextInput from '../TextInput'
import useFormChanged, {NonEmptyStringValidator, S3ObjectValidator, NonEmptyArrayValidator} from '../hooks/useFormChanged'
import {useDispatch, useSelector} from 'react-redux'
import SlimMultiSelectInput from '../SlimMultiSelectInput'
import Typography from '@mui/material/Typography'
import CustomCheckbox from '../CustomCheckbox'
import {Typography as TypographyStyle} from '../../assets/styles'
import {selectBuildingsByActiveOrganization, selectGroupsByActiveOrganization} from '../../reducers/selectors'
import RXRButton from '../RXRButton'
import GenericFileSelector from '../GenericFileSelector'
import FileHelper from '../../lib/FileHelper'
import {
  createDocument,
  deleteDocument,
  deleteBuildingDocumentByBuildingAndDocumentId,
  updateDocument,
  createBuildingDocument,
} from '../../lib/queries'
import DiscardChangesDialog from '../DiscardChangesDialog'
import {removeDocumentAndAssociatedBuildings, updateDocumentAndAssociatedBuildings} from '../../actions/globalAdminActions'

const EMPTY_STATE = {
  id: null,
  internalName: '',
  displayName: '',
  file: undefined,
  coverImage: undefined,
  // category: null, Are we using this still?
  buildingIds: [],
  residentGroups: [],
  documentTags: [],
}

function CreateOrEditDocument(props) {
  const classes = useStyles()
  const isCreateMode = !props.documentId
  const [isSaving, setIsSaving] = useState(false)
  const activeOrganizationId = useSelector(state => state.GlobalAdmin.activeOrganizationId)
  const activeOrganizationTags =
    useSelector(state => state.GlobalAdmin.organizationsLookup[state.GlobalAdmin.activeOrganizationId]).documentTags || []
  const documentsLookup = useSelector(state => state.GlobalAdmin.documentsLookup)
  const orgGroups = useSelector(selectGroupsByActiveOrganization)
  const buildingsInOrg = useSelector(selectBuildingsByActiveOrganization)
  const documentsByBuilding = useSelector(state => state.GlobalAdmin.documentsByBuilding)
  const dispatch = useDispatch()

  const thisEmptyState = {...EMPTY_STATE}
  if (isCreateMode && props.preSelectBuildingId) {
    thisEmptyState.buildingIds = [props.preSelectBuildingId]
  }

  // initialize our form controls
  const {invalidItems, formChanged, setForm, form, validateForm, resetInitialForm} = useFormChanged(
    thisEmptyState,
    useFormChanged.PropLevelValidation({
      internalName: NonEmptyStringValidator,
      displayName: NonEmptyStringValidator,
      file: S3ObjectValidator,
      coverImage: S3ObjectValidator,
    }),
  )

  // load our form based on the documentId prop
  useEffect(() => {
    if (props.documentId) {
      const selectedDocument = documentsLookup[props.documentId]
      if (!selectedDocument) {
        console.warn(`Document with ID ${props.documentId} not found in documentsLookup.`)
        return
      }

      const nextForm = {...selectedDocument}
      nextForm.buildingIds = buildingsInOrg
        .filter(b => documentsByBuilding[b.id] && documentsByBuilding[b.id].some(dId => dId === selectedDocument.id))
        .map(b => b.id)
      nextForm.documentTags = selectedDocument.documentTags || [] // Normalize to [] so selecting/deselecting tags doesn't trigger formChange (undefined/null !== [])
      resetInitialForm(nextForm)
    } else {
      resetInitialForm(thisEmptyState)
    }
  }, [props.documentId, documentsLookup])

  // This logic is kind of complicated so if we want to replicate elsewhere we should first move this to a custom hook
  // Same with Delete document logic below
  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    // indicate we're starting the save process
    setIsSaving(true)

    // convert form to more usable and formated GQL inputs
    let {document, buildingIds} = formatFormAsInputs(form)

    // create or update the document as needed
    if (isCreateMode) {
      document = await createDocument({...document, organizationId: activeOrganizationId})
    } else {
      document = await updateDocument(document)
    }

    // now handle building-document relationships
    // first determine previous building IDs associated with this document
    const previousBuildingIds = isCreateMode
      ? []
      : buildingsInOrg.filter(b => documentsByBuilding[b.id].some(dId => dId === form.id)).map(b => b.id)

    // determine which building-document relationships to add and remove
    const toRemove = previousBuildingIds.filter(id => !buildingIds.includes(id))
    const toAdd = buildingIds.filter(bId => !previousBuildingIds.includes(bId))

    // if any to remove, do that first
    if (toRemove.length > 0) {
      for (const buildingId of toRemove) {
        await deleteBuildingDocumentByBuildingAndDocumentId(buildingId, document.id)
      }
    }

    // if any to add, do that next
    if (toAdd.length > 0) {
      for (const buildingId of toAdd) {
        await createBuildingDocument({
          documentId: document.id,
          buildingId: buildingId,
        })
      }
    }

    resetInitialForm(form, () => {
      updateDocumentAndAssociatedBuildings(dispatch, document, buildingIds)
      props.onSave()
    })

    setIsSaving(false)
  }

  const handleDelete = async () => {
    if (!form.id) {
      return
    }

    setIsSaving(true)

    // first remove all building-document relationships
    const previousBuildingIds = buildingsInOrg.filter(b => documentsByBuilding[b.id].some(dId => dId === form.id)).map(b => b.id)

    if (previousBuildingIds.length > 0) {
      for (const buildingId of previousBuildingIds) {
        await deleteBuildingDocumentByBuildingAndDocumentId(buildingId, form.id)
      }
    }

    // now delete the document itself
    await deleteDocument({id: form.id})

    resetInitialForm(thisEmptyState, () => {
      removeDocumentAndAssociatedBuildings(dispatch, form.id)
      props.onDelete()
    })

    setIsSaving(false)
  }

  // Helper to split array into N columns
  function splitIntoColumns(array, columns) {
    const result = Array.from({length: columns}, () => [])
    array.forEach((item, idx) => {
      result[idx % columns].push(item)
    })
    return result
  }

  const tagColumns = splitIntoColumns(activeOrganizationTags || [], 2)

  return (
    <div className={classes.container}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextInput
            label={'Document name (Dashboard)'}
            value={form.internalName}
            onChange={v => setForm({internalName: v})}
            isRequired={true}
            error={invalidItems.includes('internalName')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            label={'Document name (App)'}
            value={form.displayName}
            onChange={v => setForm({displayName: v})}
            isRequired={true}
            error={invalidItems.includes('displayName')}
          />
        </Grid>
        <Grid item xs={12}>
          <SlimMultiSelectInput
            label={'Asset(s)'}
            onChange={v => setForm({buildingIds: v})}
            options={buildingsInOrg.map(b => ({label: b.displayName, value: b.id}))}
            value={form.buildingIds}
            error={invalidItems.includes('buildingIds')}
          />
        </Grid>
        <Grid item xs={12}>
          <SlimMultiSelectInput
            label={'Resident group(s)'}
            onChange={v => setForm({residentGroups: v})}
            options={orgGroups}
            value={form.residentGroups}
            error={invalidItems.includes('residentGroups')}
          />
        </Grid>
        {activeOrganizationTags && activeOrganizationTags.length > 0 && (
          <Grid item md={6} xs={12}>
            <Typography className={classes.textPTE}>{'Tags'}</Typography>
            <Grid container spacing={1}>
              {tagColumns.map((tags, colIdx) => (
                <Grid item xs={6} key={colIdx}>
                  {tags.map(tag => (
                    <CustomCheckbox
                      key={tag}
                      label={tag}
                      isChecked={Array.isArray(form.documentTags) ? form.documentTags.includes(tag) : false}
                      onChange={val => {
                        let nextTags = Array.isArray(form.documentTags) ? [...form.documentTags] : []
                        if (val) {
                          if (!nextTags.includes(tag)) nextTags.push(tag)
                        } else {
                          nextTags = nextTags.filter(t => t !== tag)
                        }
                        setForm({documentTags: nextTags})
                      }}
                    />
                  ))}
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <GenericFileSelector
            maxFiles={1}
            isRequired={true}
            value={form.file ? [form.file] : []}
            onChange={files => setForm({file: files[0]})}
            label={'Attach document as pdf'}
            fileTypes={['application/pdf']}
            onError={e => window.alert(e.message)}
          />
        </Grid>
        <Grid item xs={12}>
          <GenericFileSelector
            maxFiles={1}
            isRequired={true}
            value={form.coverImage ? [form.coverImage] : []}
            onChange={files => setForm({coverImage: files[0]})}
            label={'Attach cover as jpg'}
            fileTypes={FileHelper.IMAGE_FILE_TYPES}
            onError={e => window.alert(e.message)}
          />
        </Grid>
      </Grid>
      <div style={{flex: 1}}></div>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <RXRButton disabled={!formChanged} isLoading={isSaving} onClick={handleSave}>
            Save Document
          </RXRButton>
        </Grid>
        <Grid item xs={12} md={4} align={'center'}>
          <RXRButton type={RXRButton.TYPE_TEXT} isLoading={isSaving} onClick={props.onCancel}>
            Cancel
          </RXRButton>
        </Grid>
        {!isCreateMode && (
          <Grid item xs={12} md={4} align="right">
            <RXRButton type={RXRButton.TYPE_DESTRUCTIVE} isLoading={isSaving} onClick={handleDelete}>
              Delete Document
            </RXRButton>
          </Grid>
        )}
      </Grid>
      <DiscardChangesDialog hasChanges={formChanged} />
    </div>
  )
}

CreateOrEditDocument.propTypes = {
  documentId: PropTypes.string,
  preSelectBuildingId: PropTypes.string,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  onCancel: PropTypes.func,
}

/**
 * @param {*} form
 * @returns {{document: *, buildingIds: Array<string>}}
 */
function formatFormAsInputs(form) {
  const documentInput = {
    internalName: form.internalName,
    displayName: form.displayName,
    file: FileHelper.formatS3ObjectForInput(form.file),
    coverImage: FileHelper.formatS3ObjectForInput(form.coverImage),
    residentGroups: form.residentGroups,
    documentTags: form.documentTags,
  }

  if (form.id) {
    documentInput.id = form.id
  }

  return {
    document: documentInput,
    buildingIds: form.buildingIds,
  }
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  textPTE: {
    fontSize: TypographyStyle.fontSizeSmall,
    color: theme.palette.primary.main,
  },
}))

export default CreateOrEditDocument
